import { ArcElement, Chart as ChartJS, Colors, Legend, Tooltip } from "chart.js";
import { useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";
import { colorOptions } from "../constants";
import { useApiService } from "../hooks";
import { parseAmount } from "../services/utils";

ChartJS.register(ArcElement, Tooltip, Legend, Colors);

const Dashboard = () => {
  const { get } = useApiService();
  const [statsData, setStatsData] = useState({});
  const [chartsData, setChartsData] = useState({});
  const [chartsDataValues, setChartsDataValues] = useState({});

  const labels = {
    total_amounts: [
      "Upto 10Lakhs",
      "10Lakhs - 20Lakhs",
      "20Lakhs - 30Lakhs",
      "30Lakhs - 40Lakhs",
      "40Lakhs - 50Lakhs",
      "50Lakhs - 60Lakhs",
      "60Lakhs - 70Lakhs",
      "70Lakhs - 80Lakhs",
      "80Lakhs - 90Lakhs",
      "More than 90Lakhs",
    ],
    late_payments: [
      "Upto 10 months",
      "10 - 20 months",
      "20 - 30 months",
      "30 - 40 months",
      "40 - 50 months",
      "50 - 60 months",
      "60 - 70 months",
      "70 - 80 months",
      "80 - 90 months",
      "More than 90 months",
    ],
    total_emis: [
      "Upto 6 months",
      "6 - 12 months",
      "12 - 18 months",
      "18 - 24 months",
      "24 - 30 months",
      "More than 30 months",
    ],
    token_percent_paid: [
      "Upto 10%",
      "10 - 20%",
      "20 - 30%",
      "30 - 40%",
      "40 - 50%",
      "50 - 60%",
      "60 - 70%",
      "70 - 80%",
      "80 - 90%",
      "More than 90%",
    ],
  };

  const chartConfig = {
    backgroundColor: colorOptions,
    borderWidth: 0,
  };

  const chartOptions = {
    plugins: {
      legend: { display: false },
    },
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await get("/api/dashboard-data");
        if (response.status && response.data) {
          const {
            total_plots_charged_interest,
            total_revenue_generated,
            total_amounts,
            late_payments,
            total_emis,
            token_percent_paid,
          } = response.data;

          setStatsData({
            ...response.data,
            total_plots_charged_interest: parseAmount(total_plots_charged_interest),
            total_revenue_generated: parseAmount(total_revenue_generated),
          });

          const chartData = {
            total_amounts: {
              labels: labels.total_amounts,
              datasets: [{ ...chartConfig, data: Object.values(total_amounts) }],
            },
            late_payments: {
              labels: labels.late_payments,
              datasets: [{ ...chartConfig, data: Object.values(late_payments) }],
            },
            total_emis: {
              labels: labels.total_emis,
              datasets: [{ ...chartConfig, data: Object.values(total_emis) }],
            },
            token_percent_paid: {
              labels: labels.token_percent_paid,
              datasets: [{ ...chartConfig, data: Object.values(token_percent_paid) }],
            },
          };

          setChartsData(chartData);
          setChartsDataValues({
            total_amounts: Object.values(total_amounts),
            late_payments: Object.values(late_payments),
            total_emis: Object.values(total_emis),
            token_percent_paid: Object.values(token_percent_paid),
          });
        } else {
          throw new Error("Unable to fetch dashboard data");
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchDashboardData();
  }, []);

  const renderChart = (dataKey, label) => (
    <div className="dashboard-card">
      <div className="chart_container">
        {chartsData[dataKey] ? (
          chartsDataValues[dataKey].every((value) => value === "0.00") ? (
            <span className="small gray">No data added yet</span>
          ) : (
            <Pie data={chartsData[dataKey]} options={chartOptions} />
          )
        ) : (
          <span className="small gray">Loading...</span>
        )}
      </div>
      <span className="label">{label}</span>
    </div>
  );

  return (
    <div className="page_wrapper">
      <div className="stats grid_3">
        {[
          { value: statsData.total_plots_count, label: "Total Plots (All projects)" },
          { value: statsData.total_sold, label: "Total Sold (All projects)" },
          { value: statsData.total_plots_charged_interest, label: "Total Interest Charged" },
          { value: statsData.total_agents, label: "Total Agents" },
          { value: statsData.total_revenue_generated, label: "Total Revenue Generated" },
          { value: statsData.total_plots_on_hold, label: "Total Plots on Hold" },
        ].map(({ value, label }, index) => (
          <div className="dashboard-card" key={index}>
            <div className="dashboard-card--text">
              <span className="value">{value}</span>
              <span className="label">{label}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="column ">
        <div className="row start">
          <div className="grid_2 charts start">
            {renderChart("total_amounts", "Total of all amounts of plots bought")}
            {renderChart("late_payments", "Late Payment")}
            {renderChart("total_emis", "Total EMIs selected by customers")}
            {renderChart("token_percent_paid", "Token percent paid on those plots")}
          </div>
          <div className="column full">
            <h3 className="section_title text-center">Event Calendar</h3>
            <iframe
              title="calendar"
              src="https://calendar.google.com/calendar/embed?height=600&wkst=1&ctz=Asia%2FKolkata&showPrint=0&showTz=0&src=dGhlYXVydXNncm91cEBnbWFpbC5jb20&color=%23039BE5"
              style={{ border: 0 }}
              className="calendar"
              width="800"
              height="600"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
